import { Forms, Help, StringHelpers, SystemText, Tabs } from '@salesperformanceint/e4shared';
import React from 'react';
import { apiGet, apiPost } from 'services';
import { lookUpPublicKey, PublicKeyField } from './publicKey';

const st = (path, ...extra) => SystemText(`Admin/Integrations/SFTP/${path}`, ...extra);

export const AUTH_PASSWORD = 'password';
export const AUTH_SSH = 'ssh';

export const DEFAULT_PORT = 22;

// What the API sends in place of a saved password (Constants.SecretReplacementValue). It only swaps the real one
// back in for the saved server, port and username.
export const SAVED_PASSWORD = 'xxxxxxxxxxxxxxx';

// What the API answers (400) when a save would keep the saved password for a changed server or account
// (SftpSavedPassword.RequiredResponse).
export const PASSWORD_REQUIRED = 'SftpPasswordRequired';

/** The form errors for a save the API refused, from its response text; null for a refusal it doesn't explain. */
export const sftpSaveErrors = text => (text === PASSWORD_REQUIRED ? { password: st('Fields/Password/Required') } : null);

// The account we suggest customers create on their server for us; filled in when none is set.
export const SUGGESTED_USERNAME = 'richardson-prism';

export const sftpDefaults = {
    sftpPort: DEFAULT_PORT,
    sftpUsername: SUGGESTED_USERNAME,
    sftpAuthMethod: AUTH_PASSWORD,
    sftpEncrypted: false,
};

// SSH key types customers can choose when generating (values match the API's SftpSshKeyType).
export const SSH_KEY_TYPES = ['ed25519', 'rsa4096', 'ecdsaP256', 'ecdsaP384'];

export const SSH_KEY_URL = 'integrations/sftp-ssh-key';
export const PGP_KEY_URL = 'integrations/sftp-pgp-key';
export const REACHABLE_URL = 'integrations/sftp-reachable';
export const LOGIN_TEST_URL = 'integrations/sftp-login-test';

// Login test results with their own messages; any other status means the server couldn't be
// reached and uses the server check's message for it.
const LOGIN_STATUSES = ['ok', 'invalid', 'authFailed', 'noPassword', 'noKey', 'error'];

// Tabs, in order, and the error keys (from validateSftp) that belong to each.
const TABS = ['Connection', 'Authentication', 'Files'];
const TAB_ERRORS = {
    Connection: ['host', 'port'],
    Authentication: ['username', 'password', 'publicKey'],
    Files: ['files', 'encryptionKey'],
};

/**
 * Pulls the server's host name out of whatever the customer pasted, e.g.
 * "sftp://importer@SFTP.Acme.com:2222/data/" → { host: 'sftp.acme.com', port: 2222, username: 'importer' }.
 * Drops any scheme, path, query and password; port and username are returned when present.
 */
export const parseHost = value => {
    let rest = (value || '').trim();
    rest = rest.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
    rest = rest.split(/[/?#]/)[0];

    let username;
    const at = rest.lastIndexOf('@');
    if (at >= 0) {
        username = rest.slice(0, at).split(':')[0] || undefined;
        rest = rest.slice(at + 1);
    }

    let port;
    const withPort = rest.match(/^(\[[^\]]+\]|[^:]+):(\d+)$/);
    if (withPort) {
        rest = withPort[1];
        port = Number(withPort[2]);
    }

    return { host: rest.toLowerCase(), port, username };
};

/**
 * A file path as SFTP expects it: folders are separated with "/" whatever the server runs, so a "\" typed
 * between folders becomes "/", repeated separators become one, and a leading "./" goes. Applied as the
 * admin types, so the field shows the path the import will look for.
 */
export const normaliseFilePath = name =>
    (name || '')
        .replace(/\\/g, '/')
        .replace(/\/{2,}/g, '/')
        .replace(/^(\.\/)+/, '');

/**
 * Validates the SFTP fields, and that the keys they need have been generated. The connection itself
 * is checked by the Authentication tab's login test, and the files can't be checked: they won't be on
 * the server until the customer starts sending them. Resolves to an errors object keyed by field —
 * empty when valid.
 */
export const validateSftp = async (
    { sftpHost, sftpPort, sftpUsername, sftpPassword, sftpFiles, sftpAuthMethod, sftpEncrypted },
    user
) => {
    const error = field => SystemText('Admin/General/Modal/Errors/Missing', field);
    const errors = {};
    const filesParsed = (sftpFiles ? JSON.parse(sftpFiles) : []).map(file => ({
        ...file,
        name: normaliseFilePath(file.name),
    }));
    const useSsh = sftpAuthMethod === AUTH_SSH;

    const validFile = /^[\-A-Z0-9+&@#\/%?=~_|!:]+([-_]YYMM\.csv)$/gi;
    if (!sftpHost) errors.host = error(st('Fields/Host/Label'));
    if (!sftpPort) errors.port = error(st('Fields/Port/Label'));
    if (!sftpUsername) errors.username = error(st('Fields/Username/Label'));
    if (!useSsh && !sftpPassword) errors.password = error(st('Fields/Password/Label'));
    if (filesParsed?.filter(x => x.name).length === 0) {
        errors.files = error(st('Fields/Files/Label'));
    } else if (filesParsed?.filter(x => !StringHelpers.matchRegEx(validFile, x.name)).length) {
        errors.files = st('Fields/Files/Error/Name');
    }

    // A key we couldn't look up isn't reported as missing: generating one could replace the key in use.
    const keyError = async (url, missing) => {
        const result = await lookUpPublicKey(url, user);
        return result.failed ? st('Fields/KeyLookup/Error') : result.publicKey ? null : st(missing);
    };
    const sshKeyError = useSsh && (await keyError(SSH_KEY_URL, 'Fields/PublicKey/Error/Missing'));
    if (sshKeyError) errors.publicKey = sshKeyError;
    const pgpKeyError = sftpEncrypted && (await keyError(PGP_KEY_URL, 'Fields/EncryptionKey/Error/Missing'));
    if (pgpKeyError) errors.encryptionKey = pgpKeyError;

    return errors;
};

/**
 * SFTP integration form. Controlled: reads its sftp* fields from `values` (the page's draft
 * integration settings) and reports changes through `onChange(patch)`. `visible` is true while the
 * form is open: opening it checks a saved setup. Every tab can be opened at any time; a check that fails
 * (we can't see the server, or can't log in) is flagged in its tab's heading rather than locking the next tab.
 */
export const SFTP = ({ values = {}, onChange, errors = {}, user, visible = false }) => {
    const {
        sftpHost: host,
        sftpPort: port,
        sftpUsername: username,
        sftpPassword: password,
        sftpFiles: files,
        sftpAuthMethod: authMethod = AUTH_PASSWORD,
        sftpEncrypted: encrypted = false,
    } = values;
    // The saved password only works with the saved server and account, so changing the host, port or username
    // clears it, and says so, rather than leave a placeholder the API will no longer accept.
    const [passwordCleared, setPasswordCleared] = React.useState(false);
    const withPasswordCleared = (patch, changed) => {
        if (!changed || password !== SAVED_PASSWORD) return patch;
        setPasswordCleared(true);
        return { ...patch, sftpPassword: '' };
    };
    const showPasswordCleared = passwordCleared && !password;

    const setHost = v => {
        edited.current = true;
        setServerCheck(null);
        onChange(withPasswordCleared({ sftpHost: v }, v !== (host || '')));
    };
    const setPort = v => {
        edited.current = true;
        setServerCheck(null);
        onChange(withPasswordCleared({ sftpPort: v }, Number(v) !== Number(port)));
    };
    const setUsername = v => onChange(withPasswordCleared({ sftpUsername: v }, v !== (username || '')));
    const setPassword = v => onChange({ sftpPassword: v });
    const setFiles = v => onChange({ sftpFiles: v });
    const setAuthMethod = v => onChange({ sftpAuthMethod: v });
    const setEncrypted = v => onChange({ sftpEncrypted: v });

    // Light check that we can see an SFTP server at the host and port: null = not checked yet.
    // Only run when the admin changes the connection (or asks), not on load, as every integration
    // form on the page is mounted when it opens.
    const [serverCheck, setServerCheck] = React.useState(null);
    const lastCheck = React.useRef(0);
    // Whether the admin has changed the host or port since opening the form: a typed host is checked
    // when they leave the field, not on each keystroke.
    const edited = React.useRef(false);

    const checkServer = (checkHost, checkPort) => {
        const id = ++lastCheck.current;
        if (!checkHost) {
            setServerCheck(null);
            return;
        }

        const done = result => id === lastCheck.current && setServerCheck(result);
        setServerCheck({ status: 'checking' });
        apiGet(
            `${REACHABLE_URL}?host=${encodeURIComponent(checkHost)}&port=${checkPort || DEFAULT_PORT}`,
            user.guid,
            response => done(response?.status ? response : { status: 'error' }),
            () => done({ status: 'error' })
        );
    };

    // Tidy the host once the customer leaves the field: a port or username pasted into it moves to
    // its own field (the username only when that is still empty). Then check we can see the server.
    const tidyHost = () => {
        const parsed = parseHost(host);
        const patch = {};
        if (parsed.host !== (host || '')) patch.sftpHost = parsed.host;
        if (parsed.port && parsed.port !== Number(port)) patch.sftpPort = parsed.port;
        if (parsed.username && !username) patch.sftpUsername = parsed.username;
        if (Object.keys(patch).length) onChange(patch);
        checkServer(parsed.host, parsed.port || port);
    };

    const [tab, setTab] = React.useState(TABS[0]);
    const tabErrorCount = t => TAB_ERRORS[t].filter(key => errors[key]).length;

    // Login test (Authentication tab): null = not tested since the details last changed.
    const [loginTest, setLoginTest] = React.useState(null);
    const lastLoginTest = React.useRef(0);

    // The SSH key as PublicKeyField reports it: { key, generated }. A key generated in this visit
    // needs adding to the customer's server first, so it is only tested when they ask.
    const [sshKey, setSshKey] = React.useState(null);
    const onSshKeyChange = React.useCallback((key, { generated }) => setSshKey({ key, generated }), []);

    const testLogin = () => {
        const id = ++lastLoginTest.current;
        const done = result => id === lastLoginTest.current && setLoginTest(result);
        setLoginTest({ status: 'testing' });
        apiPost(
            LOGIN_TEST_URL,
            {
                host,
                port: Number(port) || DEFAULT_PORT,
                username,
                // The saved password reaches the form as a placeholder; the API swaps in the real one.
                password: authMethod === AUTH_SSH ? null : password,
                authMethod,
            },
            user.guid,
            response => done(response?.status ? response : { status: 'error' }),
            () => done({ status: 'error' }),
            'POST',
            'json'
        );
    };

    // A result only holds for the details it tested.
    React.useEffect(() => {
        lastLoginTest.current++;
        setLoginTest(null);
    }, [host, port, username, password, authMethod]);

    const connectionOk = serverCheck?.status === 'ok';

    // After a failed save, move to the first tab with errors unless the current one has some.
    React.useEffect(() => {
        if (tabErrorCount(tab) > 0) return;
        const withErrors = TABS.find(t => tabErrorCount(t) > 0);
        if (withErrors) setTab(withErrors);
    }, [errors]);

    // Opening the form checks a saved setup, so its tabs unlock as the checks pass. Not on page load,
    // as every integration form is mounted (hidden) when the page opens; and not a host being typed.
    const autoTested = React.useRef(false);
    const autoLogin = React.useRef(false);
    React.useEffect(() => {
        if (visible) return;
        // Closed: the next open starts again from Connection with fresh checks.
        edited.current = false;
        autoTested.current = false;
        autoLogin.current = false;
        lastCheck.current++;
        lastLoginTest.current++;
        setServerCheck(null);
        setLoginTest(null);
        setPasswordCleared(false);
        setTab(TABS[0]);
    }, [visible]);

    React.useEffect(() => {
        if (!visible || edited.current || !host || serverCheck) return;
        autoLogin.current = true;
        checkServer(host, port);
    }, [visible, host]);

    // Once we can see the server, test the login too: a saved password (the API uses the real one), or
    // an SSH key that already existed. A key generated in this visit needs adding to the server first.
    React.useEffect(() => {
        if (!connectionOk || loginTest || autoTested.current || !host || !username) return;
        const sshReady = authMethod === AUTH_SSH && sshKey?.key && !sshKey.generated;
        const passwordReady = authMethod !== AUTH_SSH && password;
        // A saved setup tests either; one being set up only tests an existing SSH key, once they reach Authentication.
        if (!(autoLogin.current ? sshReady || passwordReady : sshReady && tab === 'Authentication')) return;
        autoTested.current = true;
        testLogin();
    }, [connectionOk, loginTest, tab, authMethod, sshKey, host, username, password]);

    const loginMessage = result =>
        result.status === 'authFailed'
            ? st(`Fields/LoginTest/Status/authFailed/${authMethod}`, host, username)
            : LOGIN_STATUSES.includes(result.status)
            ? st(`Fields/LoginTest/Status/${result.status}`, host, username)
            : st(`Fields/ServerCheck/Status/${result.status}`, host, port || DEFAULT_PORT);

    const canTestLogin = host && username && (authMethod !== AUTH_SSH || sshKey?.key);

    // Why a tab's check failed, flagged in its heading: Connection when we can't see the server,
    // Authentication when we can't log in. Null while it hasn't run, is running, or passed.
    const failed = check => check && !['ok', 'checking', 'testing'].includes(check.status);
    const tabIssues = {
        Connection: failed(serverCheck)
            ? st(`Fields/ServerCheck/Status/${serverCheck.status}`, host, port || DEFAULT_PORT)
            : null,
        Authentication: failed(loginTest)
            ? loginMessage(loginTest)
            : showPasswordCleared
            ? st('Fields/Password/Required')
            : null,
        Files: null,
    };

    // The port box is only shown when the default port is switched off. Saved settings with a
    // non-default port open with it shown; switching back to the default resets the port.
    const [customPort, setCustomPort] = React.useState(false);
    React.useEffect(() => {
        if (port && Number(port) !== DEFAULT_PORT) setCustomPort(true);
    }, [port]);

    const getFiles = () => {
        return files ? JSON.parse(files) : [{ name: '', daily: false }];
    };

    const addFile = () => {
        const temp = [...getFiles()];
        temp.push({ name: '', daily: false });
        setFiles(JSON.stringify(temp));
    };

    const removeFile = i => {
        const temp = [...getFiles()];
        temp.splice(i, 1);
        setFiles(JSON.stringify(temp));
    };

    const setFileName = (e, i) => {
        const temp = [...getFiles()];
        temp[i].name = normaliseFilePath(e.target.value);
        setFiles(JSON.stringify(temp));
    };

    const setFileDaily = (e, i) => {
        const temp = [...getFiles()];
        temp[i].daily = e.target.checked;
        setFiles(JSON.stringify(temp));
    };

    const passwordClearedMessage = where =>
        showPasswordCleared && (
            <div className="form-group text-warning" data-testid={`sftpPasswordCleared-${where}`}>
                <i className="fa fa-info-circle mr5" />
                {st('Fields/Password/Cleared')}
            </div>
        );

    const connectionTab = (
        <>
            {/* Forms.Text doesn't take onBlur, but React's blur event bubbles to this wrapper. */}
            <div onBlur={tidyHost}>
                <Forms.Text
                    key="sftpHost"
                    data-testid="sftpHost"
                    error={errors.host}
                    mandatory
                    label={st('Fields/Host/Label')}
                    help={st('Fields/Host/Help')}
                    placeholder={st('Fields/Host/Placeholder')}
                    onChange={e => setHost(e)}
                    value={host}
                />
            </div>
            <Forms.Toggle
                key="sftpDefaultPort"
                data-testid="sftpDefaultPort"
                label={st('Fields/Port/Default/Label', DEFAULT_PORT)}
                help={st('Fields/Port/Default/Help', DEFAULT_PORT)}
                value={!customPort}
                onChange={useDefault => {
                    setCustomPort(!useDefault);
                    if (useDefault) {
                        setPort(DEFAULT_PORT);
                        if (Number(port) !== DEFAULT_PORT) checkServer(host, DEFAULT_PORT);
                    }
                }}
            />
            {customPort && (
                <div onBlur={() => checkServer(host, port)}>
                    <Forms.Text
                        key="sftpPort"
                        data-testid="sftpPort"
                        error={errors.port}
                        mandatory
                        type="number"
                        label={st('Fields/Port/Label')}
                        help={st('Fields/Port/Help')}
                        onChange={e => setPort(e)}
                        value={port}
                    />
                </div>
            )}
            {host && (
                <div className="form-group" data-testid="sftpServerCheck">
                    {serverCheck?.status === 'checking' ? (
                        <span className="flex flex-center">
                            <i className="fa fa-spinner fa-spin text-sm mr10" />
                            <span>{st('Fields/ServerCheck/Checking')}</span>
                        </span>
                    ) : serverCheck?.status === 'ok' ? (
                        <span className="flex flex-center">
                            <i
                                title={serverCheck.banner}
                                data-testid="sftpServerCheckOk"
                                className="mr10 fa text-lg fa-check-circle positive"
                            />
                            <span>{st('Fields/ServerCheck/Status/ok')}</span>
                        </span>
                    ) : serverCheck ? (
                        <span className="flex flex-center">
                            <i data-testid="sftpServerCheckFailed" className="mr10 fa text-lg fa-times-circle text-danger" />
                            <span>{st(`Fields/ServerCheck/Status/${serverCheck.status}`, host, port || DEFAULT_PORT)}</span>
                        </span>
                    ) : null}
                    {serverCheck?.status !== 'checking' && (
                        <button className="btn btn-default mt10" onClick={() => checkServer(host, port)}>
                            {st(serverCheck ? 'Fields/ServerCheck/Again' : 'Fields/ServerCheck/Check')}
                        </button>
                    )}
                </div>
            )}
            {passwordClearedMessage('Connection')}
        </>
    );

    const authenticationTab = (
        <>
            <Forms.Toggle
                key="sftpAuthMethod"
                data-testid="sftpAuthMethod"
                label={st('Fields/AuthMethod/Label')}
                help={st('Fields/AuthMethod/Help')}
                yesLabel={st('Fields/AuthMethod/Password')}
                noLabel={st('Fields/AuthMethod/SSH')}
                value={authMethod !== AUTH_SSH}
                onChange={usePassword => setAuthMethod(usePassword ? AUTH_PASSWORD : AUTH_SSH)}
            />
            <Forms.Text
                key="sftpUsername"
                data-testid="sftpUsername"
                error={errors.username}
                mandatory
                label={st('Fields/Username/Label')}
                help={st('Fields/Username/Help')}
                placeholder={SUGGESTED_USERNAME}
                onChange={e => setUsername(e)}
                value={username}
            />
            {authMethod === AUTH_SSH ? (
                <PublicKeyField
                    data-testid="sftpPublicKey"
                    url={SSH_KEY_URL}
                    textPath="Fields/PublicKey"
                    user={user}
                    error={errors.publicKey}
                    keyTypes={SSH_KEY_TYPES}
                    downloadName={`${SUGGESTED_USERNAME}.pub`}
                    onKeyChange={onSshKeyChange}
                />
            ) : (
                <>
                    <Forms.Text
                        type="password"
                        key="sftpPassword"
                        error={errors.password || (showPasswordCleared ? st('Fields/Password/Required') : undefined)}
                        data-testid="sftpPassword"
                        mandatory
                        label={st('Fields/Password/Label')}
                        help={st('Fields/Password/Help')}
                        onChange={e => setPassword(e)}
                        value={password}
                    />
                </>
            )}
            {canTestLogin && (
                <div className="form-group" data-testid="sftpLoginTest">
                    {authMethod === AUTH_SSH && sshKey?.generated && !loginTest && (
                        <div className="mb10">{st('Fields/LoginTest/NewKey')}</div>
                    )}
                    {loginTest?.status === 'testing' ? (
                        <span className="flex flex-center">
                            <i className="fa fa-spinner fa-spin text-sm mr10" />
                            <span>{st('Fields/LoginTest/Testing')}</span>
                        </span>
                    ) : loginTest?.status === 'ok' ? (
                        <span className="flex flex-center">
                            <i data-testid="sftpLoginTestOk" className="mr10 fa text-lg fa-check-circle positive" />
                            <span>{st('Fields/LoginTest/Status/ok', host, username)}</span>
                        </span>
                    ) : loginTest ? (
                        <span className="flex flex-center">
                            <i
                                title={loginTest.detail || undefined}
                                data-testid="sftpLoginTestFailed"
                                className="mr10 fa text-lg fa-times-circle text-danger"
                            />
                            <span>{loginMessage(loginTest)}</span>
                        </span>
                    ) : null}
                    {loginTest?.status !== 'testing' &&
                        (loginTest ? (
                            <button className="btn btn-default mt10" onClick={testLogin}>
                                {st('Fields/LoginTest/Again')}
                            </button>
                        ) : (
                            <button className="btn btn-default" data-testid="sftpLoginTestButton" onClick={testLogin}>
                                {st('Fields/LoginTest/Test')}
                            </button>
                        ))}
                </div>
            )}
        </>
    );

    const filesTab = (
        <>
            <div data-testid="sftpFiles" className={`form-group ${errors.files ? 'has-error' : ''}`}>
                <label>
                    <div className="flex flex-center">
                        <div className="flex-grow">
                            {st('Fields/Files/Label')}
                            <span className="mandatory">*</span>
                        </div>
                    </div>
                    <span className="form-control-help">
                        <span className="copy">{st('Fields/Files/Help')}</span>
                    </span>
                </label>
                {getFiles().map((file, i) => (
                    <div className="flex flex-center mb10" key={`file-${i}`}>
                        <span className="flex-grow mr10 ">
                            <input
                                role="textbox"
                                className="form-control"
                                value={file.name}
                                onChange={e => setFileName(e, i)}
                                placeholder={st('Fields/Files/Placeholder')}
                            />
                        </span>
                        <span className="flex flex-row flex-center">
                            <input
                                type="checkbox"
                                className="m0"
                                checked={file.daily}
                                id={`file-daily-${i}`}
                                onChange={e => setFileDaily(e, i)}
                            />
                            <label
                                htmlFor={`file-daily-${i}`}
                                className="m0 pr10 pl5"
                                aria-label={st('Fields/Daily/Help')}
                            >
                                <Help target={<span>{st('Fields/Daily/Label')}</span>} id={`daily-help-${i}`}>
                                    {st('Fields/Daily/Help')}
                                </Help>
                            </label>
                            {getFiles().length > 1 && (
                                <button onClick={() => removeFile(i)} className="btn-link" title={st('Fields/Files/Remove')}>
                                    <i className="fa fa-trash text-danger" />
                                </button>
                            )}
                        </span>
                    </div>
                ))}
                {errors.files && <span className="help-block">{errors.files}</span>}
                <button className="btn-link" onClick={addFile}>
                    {st('Fields/Files/AddAnother')}
                </button>
            </div>
            <Forms.Toggle
                key="sftpEncrypted"
                data-testid="sftpEncrypted"
                label={st('Fields/Encrypted/Label')}
                help={st('Fields/Encrypted/Help')}
                value={encrypted}
                onChange={setEncrypted}
            />
            {encrypted && (
                <PublicKeyField
                    data-testid="sftpEncryptionKey"
                    url={PGP_KEY_URL}
                    textPath="Fields/EncryptionKey"
                    user={user}
                    error={errors.encryptionKey}
                    downloadName="insights-sftp-public-key.asc"
                />
            )}
        </>
    );

    const panes = { Connection: connectionTab, Authentication: authenticationTab, Files: filesTab };

    return (
        <Tabs
            tab={tab}
            setTab={setTab}
            tabs={TABS}
            style="clean"
            tabNames={Object.fromEntries(TABS.map(t => [t, st(`Tabs/${t}`)]))}
            extras={TABS.map(t => () => {
                const count = tabErrorCount(t);
                const issue = tabIssues[t];
                return (
                    <>
                        {issue && (
                            <i
                                className="fa fa-exclamation-triangle text-danger ml5"
                                title={issue}
                                aria-label={issue}
                                data-testid={`sftpTabIssue-${t}`}
                            />
                        )}
                        {count > 0 && (
                            <span className="label label-danger ml5" data-testid={`sftpTabErrors-${t}`}>
                                {count}
                            </span>
                        )}
                    </>
                );
            })}
        >
            {/* Every pane stays mounted so a loaded key or a half-typed field survives a tab switch. */}
            {TABS.map(t => (
                <div key={t} className="mt10" data-testid={`sftpTab-${t}`} style={{ display: tab === t ? '' : 'none' }}>
                    {panes[t]}
                </div>
            ))}
        </Tabs>
    );
};

export default SFTP;
